import { h, FunctionComponent } from 'preact';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import InfoGroup from '~src/domains/common/components/InfoGroup';
import { reservationsSelector } from '~src/domains/common/state/commonSelectors';

/** Formats departure and optional arrival dates. */
const formattedTripDates = (
  departure: string | preact.JSX.Element,
  arrival?: string | preact.JSX.Element,
) => {
  return arrival ? (
    <>
      {departure}
      {' - '}
      {arrival}
    </>
  ) : (
    departure
  );
};

const StartReservations: FunctionComponent = () => {
  const { t } = useTranslation();

  const { airReservations, hotelReservations, cruiseReservations } =
    useSelector(reservationsSelector);

  const airReservationsItinerary = airReservations[0]?.itinerary || [];
  const hotelReservationsItinerary = hotelReservations[0];
  const cruiseReservationsItinerary = cruiseReservations[0]?.itinerary || [];

  /** Returns localized date from a given datestring.
   * E.g. 04071963 -> Apr 7, 1963
   * @param {string} date - Date.
   */
  const dateParse = (date: string) => {
    const parsedDay = parseInt(date.slice(6));
    const parsedMonth = parseInt(date.slice(4, 6));
    const year = date.slice(0, 4);
    const monthString = t(`common:time.months.${parsedMonth}`);

    if (parsedDay && monthString && year) {
      return (
        <Trans
          t={t}
          i18nKey='start:date'
          values={{ day: parsedDay, month: monthString, year: year }}
        />
      );
    } else {
      return 'MM/DD/YYYY';
    }
  };

  let reservationsInfo;

  // Generates trip info group for air reservations, hotel reservations, or cruise reservations, in that order.
  if (airReservationsItinerary.length) {
    const segmentCount = airReservationsItinerary.length;
    const label =
      segmentCount > 1
        ? t('start:read-only-labels.trip-dates')
        : t('start:read-only-labels.trip-date');
    const departureDate = airReservationsItinerary[0].departure_time;
    const returnDate =
      segmentCount > 1
        ? airReservationsItinerary[segmentCount - 1]?.departure_time
        : undefined;
    const parsedDepartureDate = dateParse(departureDate);
    const parsedReturnDate = returnDate && dateParse(returnDate);

    reservationsInfo = (
      <InfoGroup
        header={label}
        value={formattedTripDates(parsedDepartureDate, parsedReturnDate)}
      />
    );
  } else if (hotelReservationsItinerary) {
    const { check_in, check_out } = hotelReservationsItinerary;

    reservationsInfo = (
      <InfoGroup
        header={t('start:read-only-labels.trip-dates')}
        value={formattedTripDates(dateParse(check_in), dateParse(check_out))}
      />
    );
  } else if (cruiseReservationsItinerary.length) {
    const segmentCount = cruiseReservationsItinerary.length;
    const label =
      segmentCount > 1
        ? t('start:read-only-labels.trip-dates')
        : t('start:read-only-labels.trip-date');
    const departureDate = cruiseReservationsItinerary[0].departure_date;
    const returnDate =
      segmentCount > 1
        ? cruiseReservationsItinerary[segmentCount - 1].departure_date
        : undefined;
    const parsedDepartureDate = dateParse(departureDate);
    const parsedReturnDate = returnDate && dateParse(returnDate);

    reservationsInfo = (
      <InfoGroup
        header={label}
        value={formattedTripDates(parsedDepartureDate, parsedReturnDate)}
      />
    );
  }

  return reservationsInfo || null;
};

export default StartReservations;
